import React, { useEffect, useState, useCallback } from "react";
import "../Admin/AttendanceTable.css";
import { API_BASE_URL } from "../../apiConfig";
import { AlertCircle, Loader, ClipboardList } from "lucide-react";
import Pagination from "../Common/Pagination";

const AttendanceTable = ({ filters }) => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const rowsPerPage = 10;

  const fetchAttendanceData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const today = new Date().toISOString().split("T")[0];
      const search = filters?.searchQuery || "";
      const startDate = filters?.startDate || today;
      const endDate = filters?.endDate || today;

      const response = await fetch(
        `${API_BASE_URL}/admin/continuous-attendance-report?search=${search}&startDate=${startDate}&endDate=${endDate}&limit=${rowsPerPage}&offset=${(currentPage - 1) * rowsPerPage}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("idToken")}`,
          },
        }
      );
      console.log("AttendanceTable fetch status:", response.status);
      const data = await response.json();
      if (data.success) {
        setAttendanceData(data.data);
        setTotalRecords(data.total);
      } else {
        setError(data.message || "Failed to fetch attendance data");
        console.error("Failed to fetch attendance data:", data.message);
      }
    } catch (err) {
      setError("An error occurred while fetching data");
      console.error("Error fetching attendance data:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, rowsPerPage]);

  useEffect(() => {
    fetchAttendanceData();
  }, [fetchAttendanceData]);

  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getStatusClass = (status) => {
    switch(status?.toLowerCase()) {
      case 'present':
        return 'at-status-present';
      case 'absent':
        return 'at-status-absent';
      case 'late':
        return 'at-status-late';
      default:
        return 'at-status-default';
    }
  };

  return (
    <div className="at-container">
      {loading ? (
        <div className="at-loading">
          <Loader size={40} className="at-spinner" />
          <p>Loading attendance data...</p>
        </div>
      ) : error ? (
        <div className="at-error">
          <AlertCircle size={40} color="#ef4444" />
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button className="at-retry-btn" onClick={fetchAttendanceData}>
            Try Again
          </button>
        </div>
      ) : attendanceData.length === 0 ? (
        <div className="at-empty">
          <ClipboardList size={40} color="#6b7280" />
          <h3>No Attendance Records</h3>
          <p>No attendance records found for the selected criteria.</p>
        </div>
      ) : (
        <>
          <div className="at-table-wrapper">
            <table className="at-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Date</th>
                  <th>Check-In</th>
                  <th>Check-In Location</th>
                  <th>Check-Out</th>
                  <th>Check-Out Location</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {attendanceData.map((record, index) => (
                  <tr key={index} className="at-table-row">
                    <td className="at-name-cell">
                      <div className="at-user-avatar">
                        {record.name?.charAt(0).toUpperCase() || "?"}
                      </div>
                      {record.name}
                    </td>
                    <td>{record.email}</td>
                    <td>{record.date}</td>
                    <td>{record.checkInTime || "Not Recorded"}</td>
                    <td className="at-location-cell" data-location={record.checkInLocation || "N/A"}>
                      {record.checkInLocation || "N/A"}
                    </td>
                    <td>{record.checkOutTime || "Not Recorded"}</td>
                    <td className="at-location-cell" data-location={record.checkOutLocation || "N/A"}>
                      {record.checkOutLocation || "N/A"}
                    </td>
                    <td>
                      <span className={`at-status-badge ${getStatusClass(record.status)}`}>
                        {record.status || "Unknown"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AttendanceTable;
